<script lang="ts">
  import { onMount } from 'svelte';
  import { presentationStore, presentationClues } from '../../stores/presentationStore';
  import { socketService } from '../../services/socketService';
  import { soundService } from '../../services/soundService';
  import { renderClue } from '../../lib/clueRenderer';
  import { SoundCategory } from '../../types/audio';

  $: presentation = $presentationStore.currentPresentation;
  $: clues = $presentationClues;
  $: hasGuessedCorrectly = $presentationStore.hasGuessedCorrectly;
  $: lastGuessResult = $presentationStore.lastGuessResult;

  let guessInput = '';
  let isSubmitting = false;

  /**
   * Announce to screen reader
   */
  function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  function submitGuess() {
    if (!guessInput.trim() || isSubmitting || hasGuessedCorrectly) return;

    isSubmitting = true;
    socketService.emit('game:submitGuess', guessInput.trim());

    setTimeout(() => {
      guessInput = '';
      isSubmitting = false;
    }, 500);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      submitGuess();
    }
  }

  onMount(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.on('game:guessResult', (result) => {
      presentationStore.setGuessResult(result);

      if (result.isCorrect) {
        announceToScreenReader(`Correct! The word was ${result.correctAnswer}`, 'assertive');

        // Play correct guess sound with slight stereo variation
        soundService.play('/audio/sfx/correct-guess.ogg', {
          category: SoundCategory.SFX,
          volume: 1.0,
          pan: Math.random() * 0.4 - 0.2, // -0.2 to +0.2 for subtle variation
        });
      } else {
        announceToScreenReader('Incorrect. Try again!', 'assertive');
      }

      setTimeout(() => {
        presentationStore.clearGuessResult();
      }, 3000);
    });

    return () => {
      socket.off('game:guessResult');
    };
  });
</script>

<div class="guesser-view">
  <div class="guesser-header">
    {#if presentation}
      <h2>{presentation.username}'s Presentation</h2>
      <p class="category">Category: {presentation.category}</p>
    {/if}
  </div>

  <div class="guesser-content">
    <!-- Clues -->
    <section class="clues-section">
      <h3>Clues:</h3>
      {#if !presentation?.opener && clues.length === 0}
        <p class="empty-state">Waiting for first clue...</p>
      {:else}
        <ul class="clues-list">
          <!-- Show opener first -->
          {#if presentation?.opener}
            <li class="clue-item opener">
              <span class="clue-number">Opener</span>
              <span class="clue-text">{renderClue(presentation.opener)}</span>
            </li>
          {/if}
          <!-- Then show all other clues -->
          {#each clues as clue, index}
            <li class="clue-item">
              <span class="clue-number">#{index + 1}</span>
              <span class="clue-text">{renderClue(clue)}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    <!-- Guess input -->
    <section class="guess-section">
      {#if hasGuessedCorrectly}
        <div class="success-message" role="alert">
          <h3>✓ Correct!</h3>
          <p>You guessed: <strong>{lastGuessResult?.correctAnswer}</strong></p>
          <p class="wait-message">Wait for other players...</p>
        </div>
      {:else}
        <h3>Your Guess:</h3>
        <div class="guess-input-container">
          <input
            type="text"
            class="guess-input"
            bind:value={guessInput}
            on:keydown={handleKeyDown}
            placeholder="Type your guess..."
            disabled={isSubmitting}
            aria-label="Enter your guess"
            autofocus
          />
          <button
            class="submit-button"
            on:click={submitGuess}
            disabled={!guessInput.trim() || isSubmitting}
          >
            Guess
          </button>
        </div>

        {#if lastGuessResult && !lastGuessResult.isCorrect}
          <div class="incorrect-message" role="alert">
            Incorrect! Try again.
          </div>
        {/if}
      {/if}
    </section>
  </div>
</div>

<style>
  .guesser-view {
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem;
  }

  .guesser-header {
    text-align: center;
    padding: 2rem;
    background: white;
    border-radius: 1rem;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  h2 {
    margin: 0 0 0.5rem 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }

  .category {
    margin: 0;
    font-size: 1.125rem;
    color: #6b7280;
  }

  .guesser-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  section {
    background: white;
    border-radius: 0.75rem;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
  }

  .empty-state {
    color: #6b7280;
    text-align: center;
    padding: 1rem;
    margin: 0;
  }

  .clues-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .clue-item {
    padding: 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
    display: flex;
    gap: 1rem;
    font-size: 1.125rem;
  }

  .clue-item.opener {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border: 2px solid #92400e;
  }

  .clue-number {
    font-weight: 700;
    min-width: 4rem;
  }

  .guess-input-container {
    display: flex;
    gap: 0.75rem;
  }

  .guess-input {
    flex: 1;
    padding: 1rem;
    font-size: 1.125rem;
    border: 2px solid #d1d5db;
    border-radius: 0.5rem;
  }

  .guess-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .submit-button {
    padding: 1rem 2rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .submit-button:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .submit-button:active:not(:disabled) {
    transform: translateY(0);
  }

  .submit-button:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  .success-message {
    text-align: center;
    padding: 2rem;
    background: #d1fae5;
    border: 2px solid #10b981;
    border-radius: 0.5rem;
  }

  .success-message h3 {
    color: #059669;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  .success-message p {
    margin: 0.5rem 0;
    font-size: 1.125rem;
    color: #047857;
  }

  .wait-message {
    font-style: italic;
    opacity: 0.8;
  }

  .incorrect-message {
    margin-top: 1rem;
    padding: 0.75rem;
    background: #fee2e2;
    border: 1px solid #fecaca;
    border-radius: 0.5rem;
    color: #991b1b;
    text-align: center;
    font-weight: 600;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .guesser-view {
      padding: 1rem;
    }

    h2 {
      font-size: 1.5rem;
    }

    .clue-item {
      font-size: 1rem;
    }

    .guess-input-container {
      flex-direction: column;
    }

    .submit-button {
      width: 100%;
    }
  }
</style>
